/**
 * Bridges webapp user/plan state to window.rm_app_user and window.rm_app_config
 * so that RightMessage tag.js flows can branch on them via YesNo JS conditions.
 *
 * Call once at the app root (e.g., MainLayout). It watches authStore.user,
 * configStore.owner, and configStore.config, and triggers RM.push(['reset'])
 * whenever user/owner data changes so flows re-evaluate against fresh state.
 */
export function useRightMessageBridge() {
  const authStore = useAuthStore()
  const configStore = useConfigStore()
  const { user } = storeToRefs(authStore)
  const { owner, config, currentTeam } = storeToRefs(configStore)
  const teamStore = useTeamStore()
  const { teams } = storeToRefs(teamStore)
  const integrationStore = useIntegrationStore()
  const { integratedPartner, integratedPartnerName } = storeToRefs(integrationStore)

  let resetTimer: ReturnType<typeof setTimeout> | null = null

  // Expose a config summary as a window variable for RM flows to reference.
  // Uses a separate watch so config changes don't trigger a full flow reset.
  watch(
    [config, currentTeam, integratedPartner, teams],
    ([cfg, team, partner, t]) => {
      if (!cfg) {
        window.rm_app_config = null
        return
      }

      window.rm_app_config = {
        dimension_count: cfg.dimensions?.length ?? 0,
        campaign_count: cfg.campaigns?.length ?? 0,
        active_campaign_count: cfg.campaigns?.filter((c: any) => c.is_active)?.length ?? 0,
        widget_count: cfg.rightCta?.widgets?.length ?? 0,
        flow_count: cfg.rightCta?.offerFunnels?.length ?? 0,
        offer_count: cfg.rightCta?.offers?.length ?? 0,
        question_count: cfg.rightAsk?.questions?.length ?? 0,
        goal_count: cfg.goals?.length ?? 0,
        phrase_count: cfg.phrases?.length ?? 0,
        has_active_integration: !!partner,
        integration_name: integratedPartnerName.value ?? null,
        domain_count: cfg.domainNames?.length ?? 0,
        domains: cfg.domainNames ?? [],
        first_published_at: team?.first_published_at ?? null,
        last_published_at: team?.last_published_at ?? null,
        team_count: t?.length ?? 0,
      }
    },
    { immediate: true }
  )

  watch(
    [user, owner],
    ([u, o]) => {
      if (!u) {
        window.rm_app_user = null
        return
      }

      window.rm_app_user = {
        email: u.email,
        name: u.name,
        is_admin: u.is_admin,
        is_agency: u.is_agency,
        free_account: u.free_account,
        on_trial: o?.on_trial ?? false,
        trial_ends_at: u.trial_ends_at instanceof Date ? u.trial_ends_at.toISOString() : (u.trial_ends_at ?? null),
        plan_tier: o?.plan?.tier ?? 'grow',
        plan_features: o?.plan?.features ?? [],
        billing_model: o?.billing_model ?? 'per_team',
        is_free_builder: o?.is_free_builder ?? false,
        stripe_status: o?.stripe_status ?? null,
        has_payment_method: o?.has_payment_method ?? false,
        stripe_id: o?.stripe_id ?? null,
        created_at: o?.created_at ?? null,
        pricing_version: o?.pricing_version ?? null,
        active_subscription: o?.active_subscription ? {
          stripe_status: o.active_subscription.stripe_status,
          created_at: o.active_subscription.created_at,
        } : null,
        owned_team_count: o?.owned_teams?.length ?? 0,
      }

      // Debounce the reset so rapid user/owner updates (from separate API
      // responses resolving in different ticks) only trigger one flow run
      if (resetTimer) clearTimeout(resetTimer)
      resetTimer = setTimeout(() => {
        window.RM = window.RM || []
        window.RM.push(['reset'])
        resetTimer = null
      }, 50)

      // Identify the logged-in user to RM so their Bento subscriber data
      // carries across subdomains via the _rm_ctx cookie. Skip if already
      // identified (subscriberId in localStorage) to avoid a redundant API call.
      const hasSubscriberId = Object.keys(localStorage).some(
        k => k.startsWith('rmpanda.sources.') && k.endsWith('.subscriberId') && localStorage.getItem(k)
      )
      if (!hasSubscriberId) {
        window.RM.push(['identifyByEmail', u.email])
      }
    },
    { immediate: true }
  )
}
